import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    ONBOARDING_STEPS,
    TOUR_SCREENS,
    isExampleProject,
    matchScreenKey,
    getVisitedScreens,
    markScreenVisited,
    markOnboardingDismissed,
    isOnboardingDismissed,
    emitOnboardingEventOnce
} from './onboarding-config';

interface UseOnboardingArgs {
    /** From useCompanySetup() — null while loading. */
    hasCompany: boolean | null;
    /** The user's projects (from useAuth). */
    projects: Array<{ name?: string }>;
}

export const useOnboarding = ({ hasCompany, projects }: UseOnboardingArgs) => {
    const { pathname } = useLocation();
    const [visited, setVisited] = useState<string[]>(getVisitedScreens());

    // Record screen visits as the user navigates — drives both the "explore"
    // step and the "review example" step, whether reached via the tour or manually.
    useEffect(() => {
        const key = matchScreenKey(pathname);
        if (key && !visited.includes(key)) {
            markScreenVisited(key);
            setVisited((prev) => (prev.includes(key) ? prev : [...prev, key]));
        }
    }, [pathname, visited]);

    const hasOwnProject = Array.isArray(projects) && projects.some((p) => !isExampleProject(p));
    const allScreensVisited = TOUR_SCREENS.every((s) => visited.includes(s.key));

    const completion: Record<string, boolean> = {
        company: hasCompany === true,
        review_example: visited.includes('review_1'),
        create_project: hasOwnProject,
        explore: allScreensVisited
    };

    const completedCount = ONBOARDING_STEPS.filter((step) => completion[step.id]).length;
    const total = ONBOARDING_STEPS.length;
    const allComplete = completedCount === total;

    // Funnel start — counted once, only for users who still have work to do, so
    // the completion rate (completed / started) is not skewed by already-set-up users.
    useEffect(() => {
        if (!allComplete) {
            emitOnboardingEventOnce('started', { event: 'onboarding_started', total_steps: total });
        }
    }, [allComplete, total]);

    // Per-step completion — counted once per step as its signal turns true.
    useEffect(() => {
        ONBOARDING_STEPS.forEach((step) => {
            if (completion[step.id]) {
                emitOnboardingEventOnce(`step_${step.id}`, {
                    event: 'onboarding_step_completed',
                    step_id: step.id
                });
            }
        });
    }, [hasCompany, hasOwnProject, visited]);

    // Funnel completion — the numerator for the >=60% target.
    useEffect(() => {
        if (allComplete) {
            emitOnboardingEventOnce('completed', { event: 'onboarding_completed', total_steps: total });
        }
    }, [allComplete, total]);

    // Seed from the persisted flag so the widget stays dismissed across reloads
    // and new tabs — otherwise it re-appears on every full page load.
    const [dismissed, setDismissed] = useState<boolean>(() => isOnboardingDismissed());
    const dismiss = () => {
        markOnboardingDismissed();
        setDismissed(true);
    };

    return {
        steps: ONBOARDING_STEPS,
        screens: TOUR_SCREENS,
        completion,
        visited,
        visitedCount: TOUR_SCREENS.filter((s) => visited.includes(s.key)).length,
        screenTotal: TOUR_SCREENS.length,
        completedCount,
        total,
        allComplete,
        dismissed,
        dismiss
    };
};
